import { Autocomplete, FormControlLabel, Grid, IconButton, InputAdornment, Radio, RadioGroup, TextField, Typography } from '@mui/material'
import GlobalNavbar from '../../../../GlobalComponent/GlobalNavbar/GlobalNavbar'
import { useNavigate, useSearchParams } from 'react-router-dom'
import globalStyles from '../../../../Styles/ComponentStyles/global.module.scss';
import styles from '../../../../Styles/ComponentStyles/ProfileStyle/ProfileNewAnnuity.module.scss';
import Astrict from '../../../../GlobalComponent/Astrict/Astrict';
import GlobalTextfield from '../../../../GlobalComponent/GlobalTextfield/GlobalTextfield';
import IconsColor from '../../../../Utils/IconsColor';
import GlobalButton from '../../../../Utils/GlobalUtilsComponents/GlobalButton';
import { useContext, useEffect, useState } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import { UserContext, UserContextPayload } from '../../../../Context/UserDetailContext';
import { useSnackbar } from '../../../../GlobalComponent/GlobalSnackbar/GlobalSnackbar';
import agent from '../../../../service/api';
import { ProfileContext, ProfileContextPayload } from '../../../../Context/ProfileDetailsContext';
import { formatWithThousandSeparator } from '../../../../Utils/HelperFunctions';

/* ------------------------------------------------------------------ */
/* Shared constants / validators (module scope = no re-creation)       */
/* ------------------------------------------------------------------ */

const NAME_REGEX = /^[A-Za-z0-9 ]{1,30}$/;
const CONTACT_REGEX = /^\d{10}$/;
const EMAIL_REGEX = /^([a-zA-Z0-9._]+)@([a-zA-Z0-9-]+)\.([a-zA-Z]{2,})(\.[a-zA-Z]{2,})?$/;

// Fields whose values are formatted with a thousands separator.
const FORMAT_FIELDS = new Set([
    'accumulationBalanceAccount',
    'accumulationAnnualContribution',
    'currentAccountBalance',
    'employerMatch'
]);

// Fields constrained to a 0–100 percentage range.
const PERCENT_FIELDS = new Set([
    'userAccumulationInterestRate',
    'userAccumulationAnnualFees',
    'percentFees',
    'rateOfReturnDuringRetirement',
    'rateOfReturnDuringWorkingYears',
    'incomeTaxRateBeforeRetirement',
    'incomeTaxRateDuringRetirement'
]);

const isFilled = (value: any) => Boolean(String(value ?? '').trim());

// Per-section rule: returns true when the field value is valid and its error can be cleared.
const FIELD_CLEAR_RULES: Record<string, Record<string, (value: any) => boolean>> = {
    universalInput: {
        firstName: (v) => NAME_REGEX.test(String(v ?? '').trim()),
        currentAge: (v) => v >= 0 && v <= 120,
        ageAtRetirement: (v) => v >= 0 && v <= 120,
        Contact: (v) => CONTACT_REGEX.test(v),
        email: (v) => EMAIL_REGEX.test(v)
    },
    accumulationData: {
        accumulationBalanceAccount: isFilled,
        accumulationAnnualContribution: isFilled,
        userAccumulationYear: isFilled,
        userAccumulationInterestRate: isFilled,
        userAccumulationAnnualFees: isFilled
    },
    typicalPlan: {
        currentAccountBalance: isFilled,
        percentFees: isFilled,
        employerMatch: isFilled,
        rateOfReturnDuringRetirement: isFilled,
        rateOfReturnDuringWorkingYears: isFilled,
        incomeTaxRateBeforeRetirement: isFilled,
        incomeTaxRateDuringRetirement: isFilled
    }
};

const REQUIRED_ACCUMULATION_FIELDS = [
    { field: 'accumulationBalanceAccount', message: 'Current balance account is required' },
    { field: 'accumulationAnnualContribution', message: 'Annual contribution is required' },
    { field: 'userAccumulationYear', message: 'Number of years is required' },
    { field: 'userAccumulationInterestRate', message: 'Interest rate is required' },
    { field: 'userAccumulationAnnualFees', message: 'Annual fees is required' }
];

const REQUIRED_TYPICAL_FIELDS = [
    { field: 'currentAccountBalance', message: 'Current account balance is required' },
    { field: 'percentFees', message: 'Percent fees is required' },
    { field: 'employerMatch', message: 'Employer match is required' },
    { field: 'rateOfReturnDuringRetirement', message: 'Rate of return during retirement is required' },
    { field: 'rateOfReturnDuringWorkingYears', message: 'Rate of return during working years is required' },
    { field: 'incomeTaxRateBeforeRetirement', message: 'Income tax rate before retirement is required' },
    { field: 'incomeTaxRateDuringRetirement', message: 'Income tax rate during retirement is required' }
];

const formatSeparatorValue = (rawValue: string) => {
    const value = String(rawValue ?? '').replaceAll(',', '');
    if (!Number.isNaN(Number(value)) && value.trim() !== '') {
        return Number(value).toLocaleString();
    }
    return value;
};

let rowSequence = 0;
const generateRowId = (): string => `row-${++rowSequence}`;

// Validation helpers split out of validateProfileForm to keep cognitive complexity low.
const validateUniversalInput = (data: any, requireContactAndEmail = true) => {
    const errors: any = {};
    if (!data) {
        return { errors, isValid: false };
    }
    let isValid = true;

    if (!data.firstName?.trim()) {
        errors.firstName = 'First name is required';
        isValid = false;
    } else if (!NAME_REGEX.test(data.firstName)) {
        errors.firstName = 'Please enter a valid firstname';
        isValid = false;
    }

    if (!data.currentAge) {
        errors.currentAge = 'Current age is required';
        isValid = false;
    } else if (data.currentAge < 0 || data.currentAge > 120) {
        errors.currentAge = 'Please enter a valid age between 0 and 120';
        isValid = false;
    }

    if (!data.ageAtRetirement) {
        errors.ageAtRetirement = 'Retirement age is required';
        isValid = false;
    } else if (data.ageAtRetirement <= data.currentAge) {
        errors.ageAtRetirement = 'Retirement age must be greater than current age';
        isValid = false;
    }

    if (requireContactAndEmail) {
        if (!data.Contact?.trim()) {
            errors.Contact = 'Contact number is required';
            isValid = false;
        } else if (!CONTACT_REGEX.test(data.Contact)) {
            errors.Contact = 'Please enter a valid 10-digit contact number';
            isValid = false;
        }

        if (!data.email?.trim()) {
            errors.email = 'Email is required';
            isValid = false;
        } else if (!EMAIL_REGEX.test(data.email)) {
            errors.email = 'Please enter a valid email address';
            isValid = false;
        }
    }

    return { errors, isValid };
};

const validateRequiredFields = (data: any, requiredFields: { field: string; message: string }[], allowZero = false) => {
    const errors: any = {};
    if (!data) {
        return { errors, isValid: false };
    }
    let isValid = true;
    requiredFields.forEach(({ field, message }) => {
        const value = data[field];
        const missing = allowZero ? (!value && value !== 0) : !value;
        if (missing) {
            errors[field] = message;
            isValid = false;
        }
    });
    return { errors, isValid };
};

const validateContributionData = (contributionData: any) => {
    if (contributionData?.length <= 0) {
        return { error: 'At least one input for contribution data is required', isValid: false };
    }

    const itemErrorsList: any[] = [];
    let isValid = true;

    contributionData.forEach((item: any, index: number) => {
        const itemErrors: any = {};
        if (!item.annualContribution?.trim()) {
            itemErrors.annualContribution = 'This field is required';
            isValid = false;
        }
        if (!item.duration?.trim()) {
            itemErrors.duration = 'This field is required';
            isValid = false;
        }
        if (Object.keys(itemErrors).length > 0) {
            itemErrorsList[index] = itemErrors;
        }
    });

    return { error: itemErrorsList.length > 0 ? itemErrorsList : undefined, isValid };
};

const validateTypicalPlan = (data: any) => {
    if (!data) {
        return { errors: {}, isValid: false };
    }

    const { errors, isValid: requiredValid } = validateRequiredFields(data, REQUIRED_TYPICAL_FIELDS, true);
    let isValid = requiredValid;

    const contribution = validateContributionData(data.user_tpi_contribution_data);
    if (!contribution.isValid) {
        isValid = false;
    }
    if (contribution.error !== undefined) {
        errors.user_tpi_contribution_data = contribution.error;
    }

    return { errors, isValid };
};

const ProfileNewAnnuityAccumulation = () => {

    const {
        userDetail,
        setFlagLoader
    } = useContext(UserContext) as UserContextPayload

    const {
        updateUserProfileAPI,
        createNewUserProfile,
        getparticularUserProfileDetail
    } = useContext(ProfileContext) as ProfileContextPayload

    const userRole = userDetail?.user_data?.user_role_type?.toLowerCase();
    const { showSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const userId = searchParams.get('user_id');
    const isEditMode = Boolean(userId);

    const initialContributionRow = { rowId: generateRowId(), annualContribution: '', duration: '' };
    const [annualPremiumTypical, setAnnualPremiumTypical] = useState<any>([initialContributionRow]);
    const [agentList, setAgentList] = useState<any>([])
    const [formErrors, setFormErrors] = useState<any>({});
    const [flagSampleProfile, setFlagSampleProfile] = useState(false);
    const [flagSaveAsSampleProfile, setFlagSaveAsSampleProfile] = useState(false);

    const [finalAccumulationData, setFinalAccumulationData] = useState({
        universalInput: {
            firstName: '',
            currentAge: '',
            ageAtRetirement: '',
            Contact: '',
            email: ''
        },
        accumulationData: {
            accumulationBalanceAccount: "",
            accumulationAnnualContribution: "",
            userAccumulationYear: "",
            userAccumulationInterestRate: "",
            userAccumulationAnnualFees: ""
        },
        typicalPlan: {
            user_tpi_contribution_data: [{ ...initialContributionRow }],
            user_tpi_monthly_flag: true,
            user_tpi_annually_flag: false,
            currentAccountBalance: '',
            percentFees: '',
            employerMatch: '',
            rateOfReturnDuringRetirement: '',
            rateOfReturnDuringWorkingYears: '',
            incomeTaxRateBeforeRetirement: '',
            incomeTaxRateDuringRetirement: ''
        },
        AssignProfile: {
            agentId: null,
            agentName: ''
        },
        sample_profile: false
    })

    // Reusable key handlers (also fixes the duplicated [0-9] regexes -> \d).
    const allowOnlyDigits = (e: any) => {
        if (!/\d$/.test(e.key)) {
            e.preventDefault();
        }
    };

    const allowDigitsAndDot = (e: any) => {
        const value = e.target.value;
        if (!/^[\d.]$/.test(e.key) || (e.key === '.' && value.includes('.'))) {
            e.preventDefault();
        }
    };

    const handleCancel = () => {
        navigate(-1)
    }

    const handleAgentChange = (_event: React.SyntheticEvent, newValue: any) => {
        handleInputChange("AssignProfile", "agentId", newValue?.value);
        handleInputChange("AssignProfile", "agentName", newValue?.label);
    };

    const handleAddIncomeSource = () => {
        const newRow = { rowId: generateRowId(), annualContribution: '', duration: '' };
        setAnnualPremiumTypical((prev: any) => [...prev, newRow]);
        setFinalAccumulationData((prev: any) => ({
            ...prev,
            typicalPlan: {
                ...prev.typicalPlan,
                user_tpi_contribution_data: [
                    ...prev.typicalPlan.user_tpi_contribution_data,
                    newRow
                ]
            }
        }));
    }

    const handleRemoveSource = (index: number) => {
        if (annualPremiumTypical.length > 1) {
            setAnnualPremiumTypical((prev: any) => prev.filter((_: any, i: any) => i !== index));
            setFinalAccumulationData((prev: any) => ({
                ...prev,
                typicalPlan: {
                    ...prev.typicalPlan,
                    user_tpi_contribution_data: prev.typicalPlan.user_tpi_contribution_data.filter((_: any, i: any) => i !== index)
                }
            }));
        }
    }

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value == "monthly") {
            handleInputChange("typicalPlan", "user_tpi_monthly_flag", true)
            handleInputChange("typicalPlan", "user_tpi_annually_flag", false)
        } else if (value == "annually") {
            handleInputChange("typicalPlan", "user_tpi_monthly_flag", false)
            handleInputChange("typicalPlan", "user_tpi_annually_flag", true)
        }
    };

    const handleArrayInputChange = (
        section: any,
        arrayField: any,
        index: number,
        field: string,
        rawValue: any
    ) => {
        const value = field === "annualContribution" ? formatSeparatorValue(rawValue) : rawValue;

        setAnnualPremiumTypical((prev: any) =>
            prev.map((item: any, i: any) =>
                i === index ? { ...item, [field.toLowerCase()]: value } : item
            )
        );
        setFinalAccumulationData((prev: any) => ({
            ...prev,
            [section]: {
                ...prev[section],
                [arrayField]: prev[section][arrayField].map((item: any, i: number) =>
                    i === index ? { ...item, [field]: value } : item
                )
            }
        }));
        setFormErrors((prevErrors: any) => {
            if (section !== "typicalPlan" || !value.trim()) {
                return prevErrors;
            }
            const updatedErrors = { ...prevErrors };
            updatedErrors.typicalPlan = updatedErrors.typicalPlan || {};
            const currentErrors = updatedErrors.typicalPlan.user_tpi_contribution_data || [];
            currentErrors[index] = currentErrors[index] || {};

            delete currentErrors[index][field];

            if (Object.keys(currentErrors[index]).length === 0) {
                currentErrors.splice(index, 1);
            }

            updatedErrors.typicalPlan.user_tpi_contribution_data = currentErrors.length > 0 ? currentErrors : undefined;
            return updatedErrors;
        });
    }

    // Clears the error for a single field once its value becomes valid.
    const clearFieldError = (prevErrors: any, section: any, field: string, value: any) => {
        const rule = FIELD_CLEAR_RULES[section]?.[field];
        if (!rule?.(value)) {
            return prevErrors;
        }
        const updatedErrors = { ...prevErrors };
        if (updatedErrors[section]) {
            delete updatedErrors[section][field];
        }
        return updatedErrors;
    };

    const handleInputChange = (section: any, field: string, value: any) => {
        let nextValue = value;

        if (FORMAT_FIELDS.has(field)) {
            nextValue = formatSeparatorValue(nextValue);
        }
        if (PERCENT_FIELDS.has(field) && (Number(nextValue) < 0 || Number(nextValue) > 100)) {
            return;
        }

        setFinalAccumulationData((prev: any) => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: nextValue
            }
        }));
        setFormErrors((prevErrors: any) => clearFieldError(prevErrors, section, field, nextValue));
    }

    const validateProfileForm = (finalAnnuityRothData: any, flagSaveAsSample: boolean) => {
        const requireContactAndEmail = !finalAnnuityRothData?.sample_profile || flagSaveAsSample;
        const universal = validateUniversalInput(finalAnnuityRothData.universalInput, requireContactAndEmail);
        const accumulation = validateRequiredFields(finalAnnuityRothData.accumulationData, REQUIRED_ACCUMULATION_FIELDS);
        const typical = validateTypicalPlan(finalAnnuityRothData.typicalPlan);

        const errors = {
            universalInput: universal.errors,
            accumulationData: accumulation.errors,
            typicalPlan: typical.errors
        };
        const isValid = universal.isValid && accumulation.isValid && typical.isValid;

        return { isValid, errors };
    }

    const transformedDataForCreate = (data: any, flagSaveAsSample: boolean) => {
        const transformedData = {
            user_profile_type_id: 4,

            // universal input
            user_name: data?.universalInput?.firstName,
            user_current_age: data?.universalInput?.currentAge,
            user_retirement_age: data?.universalInput?.ageAtRetirement,
            user_contact: data?.universalInput?.Contact,
            user_email: data?.universalInput?.email,

            // Roth Conversion data
            user_accumulation_balance_account: data?.accumulationData?.accumulationBalanceAccount,
            user_accumulation_annual_contribution: data?.accumulationData?.accumulationAnnualContribution,
            user_accumulation_year: data?.accumulationData?.userAccumulationYear,
            user_accumulation_interest_rate: data?.accumulationData?.userAccumulationInterestRate,
            user_accumulation_annual_fees: data?.accumulationData?.userAccumulationAnnualFees,

            user_tpi_contribution_data: data?.typicalPlan?.user_tpi_contribution_data?.map((item: any) => ({
                total_contribution: item?.annualContribution,
                duration: item?.duration
            })),

            // typical plan
            user_tpi_monthly_flag: data?.typicalPlan?.user_tpi_monthly_flag,
            user_tpi_annually_flag: data?.typicalPlan?.user_tpi_annually_flag,
            user_tpi_curr_account_balance: data?.typicalPlan?.currentAccountBalance,
            user_tpi_percent_fees: data?.typicalPlan?.percentFees,
            user_tpi_employer_match: data?.typicalPlan?.employerMatch,
            user_tpi_rate_of_return_dr: data?.typicalPlan?.rateOfReturnDuringRetirement,
            user_tpi_rate_of_return_br: data?.typicalPlan?.rateOfReturnDuringWorkingYears,
            user_tpi_it_rate_br: data?.typicalPlan?.incomeTaxRateBeforeRetirement,
            user_tpi_it_rate_dr: data?.typicalPlan?.incomeTaxRateDuringRetirement,

            // assigned agent
            user_iul_assigned_agent_id: data?.AssignProfile?.agentId ? [data?.AssignProfile?.agentId] : [],

            sample_profile_flag: !flagSaveAsSample && Boolean(data?.sample_profile),
        }
        return transformedData
    }

    const buildApiPayload = (isUpdate: boolean) => {
        const payload: any = transformedDataForCreate(finalAccumulationData, flagSaveAsSampleProfile);

        if (isUpdate) {
            payload.user_profile_id = userId;
        }

        if (finalAccumulationData?.sample_profile && !flagSaveAsSampleProfile) {
            payload.user_email = '';
            payload.user_contact = '';
            payload.user_iul_assigned_agent_id = [];
        }

        return payload;
    };

    const handleProfileFormSubmit = async () => {
        try {
            const { isValid, errors } = validateProfileForm(finalAccumulationData, flagSaveAsSampleProfile);

            if (!isValid) {
                setFormErrors(errors);
                showSnackbar("Please fill in all required fields correctly", "error");
                return;
            }

            setFlagLoader(true);

            const isUpdate = isEditMode && !flagSaveAsSampleProfile;
            const apiPayload = buildApiPayload(isUpdate);

            const response = isUpdate
                ? await updateUserProfileAPI(apiPayload)
                : await createNewUserProfile(apiPayload);

            if (response?.status) {
                const profileId = isUpdate ? userId : response?.data?.[0]?.user_profile_id;
                navigate(`/profile/annuityProfileDetails/annuityAccumulation?user_id=${profileId}&tab=1`);
                showSnackbar(`Profile ${isUpdate ? 'Updated' : 'Created'} Successfully!`, "success");
            } else {
                showSnackbar(response?.error?.message || "Error creating profile", "error");
            }
        } catch (error) {
            console.log(error, "Error submitting data!");
        } finally {
            setFlagLoader(false);
            setFlagSaveAsSampleProfile(false);
        }
    }

    useEffect(() => {
        try {
            setFlagLoader(true)
            const getAgentListData = async () => {
                const response = await agent?.getAgentDetailsAPI?.get()
                if (response?.status) {
                    const agents = response?.data?.[0]?.agents;

                    if (!agents) {
                        setAgentList([]);
                        setFlagLoader(false);
                        return;
                    }

                    const filteredAgents = agents
                        .filter((agentItem: any) => agentItem?.user_status_name?.toLowerCase() === "active" && agentItem?.user_role_type?.toLowerCase() === "agent")
                        .map((agentItem: any) => ({
                            label: `${agentItem.user_firstname} ${agentItem.user_lastname}`,
                            value: agentItem.user_id
                        }));
                    setAgentList(filteredAgents);
                    setFlagLoader(false);
                } else {
                    showSnackbar(response?.error?.message || "Something went wrong!", "error")
                    setFlagLoader(false)
                }
            }
            if (userRole == "admin") {
                getAgentListData();
            }
        } catch (error) {
            console.log(error, "Error fetching data")
            setFlagLoader(false)
        } finally {
            setFlagLoader(false)
        }
    }, [])

    useEffect(() => {
        if (isEditMode) {
            const fetchParticularUserProfileDetail = async () => {
                try {
                    setFlagLoader(true);
                    const response = await getparticularUserProfileDetail(userId);

                    if (response?.status && response?.inputData?.[0]) {
                        const userData = response.inputData[0];

                        if (userData?.sample_profile) {
                            setFlagSampleProfile(true);
                        }

                        const emailValue = userData?.sample_profile ? '' : userData?.universalInput?.email;
                        const contactValue = userData?.sample_profile ? '' : userData?.universalInput?.Contact;

                        if (userData.typicalPlan?.user_tpi_contribution_data?.length > 0) {
                            setAnnualPremiumTypical(
                                userData.typicalPlan.user_tpi_contribution_data.map((item: any) => ({
                                    rowId: item.rowId ?? generateRowId(),
                                    annualContribution: item.annualContribution || '',
                                    duration: item.duration || ''
                                }))
                            );
                        }
                        const formattedTypicalPlan = {
                            ...userData.typicalPlan,
                            currentAccountBalance: formatWithThousandSeparator(userData.typicalPlan.currentAccountBalance),
                            employerMatch: formatWithThousandSeparator(userData.typicalPlan.employerMatch),
                            user_tpi_contribution_data: userData.typicalPlan.user_tpi_contribution_data?.map((item: any) => ({
                                ...item,
                                rowId: item.rowId ?? generateRowId(),
                                annualContribution: formatWithThousandSeparator(item.annualContribution)
                            }))
                        };

                        setFinalAccumulationData({
                            ...userData,
                            universalInput: {
                                ...userData?.universalInput,
                                email: emailValue,
                                Contact: contactValue
                            },
                            accumulationData: {
                                ...userData?.accumulationData,
                                accumulationBalanceAccount: formatWithThousandSeparator(userData?.accumulationData?.accumulationBalanceAccount),
                                accumulationAnnualContribution: formatWithThousandSeparator(userData?.accumulationData?.accumulationAnnualContribution),
                            },
                            typicalPlan: formattedTypicalPlan,
                        });
                    }
                } catch (error) {
                    console.error("Error fetching user profile:", error);
                } finally {
                    setFlagLoader(false);
                }
            };

            fetchParticularUserProfileDetail();
        }
    }, [userId, isEditMode]);

    useEffect(() => {
        if (flagSaveAsSampleProfile) {
            handleProfileFormSubmit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flagSaveAsSampleProfile]);

    return (
        <Grid container>
            <Grid item xs={12}>
                <GlobalNavbar
                    navbarBtnText={''}
                    navbarText={isEditMode ? "Edit Profile" : "Create New Accumulation Profile"}
                    flagSaveCancelBtn
                    flagHelperTooltip
                    primaryBtnClick={handleProfileFormSubmit}
                    secondaryBtnClick={handleCancel}
                    saveAsSampleProfileClick={() => { setFlagSaveAsSampleProfile(true) }}
                    iseEditMode={isEditMode}
                    flagSampleProfile={flagSampleProfile}
                    flagBorderBottom={true}
                />
            </Grid>

            <Grid item xs={12} className={`${styles.newAnnuityMainItem}`}>
                <Grid container>
                    {/* UNIVERSAL INPUT */}
                    <Grid item xs={12} className={`${globalStyles.globalPFBlurWrapper}`}>
                        <Grid container>
                            <Grid item xs={12} className={`${globalStyles.globalPFSectioHeadItem}`}>
                                <Typography
                                    component={'span'}
                                    className={`${globalStyles.globalPFlabel}`}
                                >
                                    Universal Inputs
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid container className={`${globalStyles.globalPFFieldContainer}`}>
                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Name <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder='Enter Name'
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onChange={(e) => handleInputChange('universalInput', 'firstName', e.target.value)}
                                                    value={finalAccumulationData?.universalInput?.firstName}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.universalInput?.firstName}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Age at Retirement <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder='Enter Age at Retirement'
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onChange={(e) => handleInputChange('universalInput', 'ageAtRetirement', e.target.value)}
                                                    value={finalAccumulationData?.universalInput?.ageAtRetirement}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.universalInput?.ageAtRetirement}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Current Age <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder='Enter Age'
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onChange={(e) => handleInputChange('universalInput', 'currentAge', e.target.value)}
                                                    value={finalAccumulationData?.universalInput?.currentAge}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.universalInput?.currentAge}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Cell Phone <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder={`${finalAccumulationData?.sample_profile ? '' : 'Enter Cell Phone'}`}
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onChange={(e) => handleInputChange('universalInput', 'Contact', e.target.value)}
                                                    value={finalAccumulationData?.universalInput?.Contact}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.universalInput?.Contact}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Email <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder={`${finalAccumulationData?.sample_profile ? '' : 'Enter Email'}`}
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onChange={(e) => handleInputChange('universalInput', 'email', e.target.value)}
                                                    value={finalAccumulationData?.universalInput?.email}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.universalInput?.email}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                        </Grid>
                    </Grid>

                    {/* Annuity - Accumulation */}
                    <Grid item xs={12} className={`${globalStyles.globalPFBlurWrapper}`}>
                        <Grid container>
                            <Grid item xs={12} className={`${globalStyles.globalPFSectioHeadItem}`}>
                                <Typography
                                    component={'span'}
                                    className={`${globalStyles.globalPFlabel}`}
                                >
                                    Annuity - Accumulation
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid container className={`${globalStyles.globalPFFieldContainer}`}>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Current balance account <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <IconsColor iconName="dollarIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    onKeyPress={allowOnlyDigits}
                                                    onChange={(e) => handleInputChange('accumulationData', 'accumulationBalanceAccount', e.target.value)}
                                                    value={finalAccumulationData?.accumulationData?.accumulationBalanceAccount}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.accumulationData?.accumulationBalanceAccount}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Annual Contribution <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <IconsColor iconName="dollarIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    onKeyPress={allowOnlyDigits}
                                                    onChange={(e) => handleInputChange('accumulationData', 'accumulationAnnualContribution', e.target.value)}
                                                    value={finalAccumulationData?.accumulationData?.accumulationAnnualContribution}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.accumulationData?.accumulationAnnualContribution}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    # Years <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder='Enter Number of Years'
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onChange={(e) => handleInputChange('accumulationData', 'userAccumulationYear', e.target.value)}
                                                    value={finalAccumulationData?.accumulationData?.userAccumulationYear}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.accumulationData?.userAccumulationYear}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Interest Rate (%) <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder=''
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    onKeyPress={allowDigitsAndDot}
                                                    onChange={(e) => handleInputChange('accumulationData', 'userAccumulationInterestRate', e.target.value)}
                                                    value={finalAccumulationData?.accumulationData?.userAccumulationInterestRate}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.accumulationData?.userAccumulationInterestRate}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Annual Fees (%) <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder=''
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    onKeyPress={allowDigitsAndDot}
                                                    onChange={(e) => handleInputChange('accumulationData', 'userAccumulationAnnualFees', e.target.value)}
                                                    value={finalAccumulationData?.accumulationData?.userAccumulationAnnualFees}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.accumulationData?.userAccumulationAnnualFees}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* TYPICAL PLAN */}
                    <Grid item xs={12} className={`${globalStyles.globalPFBlurWrapper}`}>
                        <Grid container>
                            <Grid item xs={12} className={`${globalStyles.globalPFSectioHeadItem}`}>
                                <Grid container sx={{ alignItems: 'center', justifyContent: "space-between" }}>
                                    <Grid item >
                                        <Typography
                                            component={'span'}
                                            className={`${globalStyles.globalPFlabel}`}
                                        >
                                            Typical Plan Inputs
                                        </Typography>
                                    </Grid>
                                    <Grid item className={styles.newAnnualBtnItem}>
                                        <GlobalButton
                                            fullWidth
                                            variant='contained'
                                            value='New Contribution'
                                            size='medium'
                                            className={`${globalStyles.globalButtonStyleV1} ${styles.newAnnualBtn}`}
                                            startIcon={<IconsColor iconName="plusIcon" width="13px" height="13px" />}
                                            onClick={() => { handleAddIncomeSource() }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid container className={`${globalStyles.globalPFFieldContainer}`}>
                                    <Grid item xs={12}>
                                        <RadioGroup
                                            row
                                            aria-labelledby="demo-radio-buttons-group-label"
                                            name="radio-buttons-group"
                                            onChange={(e) => handleRadioChange(e)}
                                            sx={{
                                                "& .MuiButtonBase-root": {
                                                    padding: "9px 4px 9px 9px !important"
                                                }
                                            }}
                                        >
                                            <FormControlLabel
                                                value="monthly"
                                                control={<Radio
                                                    sx={{
                                                        color: "#3B78CE",
                                                        '&.Mui-checked': {
                                                            color: "#3B78CE",
                                                        },
                                                        "& .MuiFormControlLabel-root": { color: '#3B78CE' },
                                                    }}
                                                />}
                                                sx={{
                                                    color: '#314158',
                                                    "& .MuiTypography-root": {
                                                        fontWeight: '600 !important'
                                                    }
                                                }}
                                                label="Monthly"
                                                checked={finalAccumulationData?.typicalPlan?.user_tpi_monthly_flag}
                                            />

                                            <FormControlLabel
                                                value="annually"
                                                control={<Radio
                                                    sx={{
                                                        color: "#3B78CE",
                                                        '&.Mui-checked': {
                                                            color: "#3B78CE",
                                                        },

                                                    }}
                                                />}
                                                sx={{
                                                    color: '#314158',
                                                    "& .MuiTypography-root": {
                                                        fontWeight: '600 !important'
                                                    }
                                                }}
                                                label="Annually"
                                                checked={finalAccumulationData?.typicalPlan?.user_tpi_annually_flag}
                                            />
                                        </RadioGroup>
                                    </Grid>

                                    {annualPremiumTypical.map((source: any, index: any) => {
                                        return (
                                            <Grid item xs={12} key={source.rowId}>
                                                <Grid container sx={{ alignItems: 'center' }} className={`${globalStyles.globalPFFieldContainer}`}>
                                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                        <Grid container>
                                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                                    Contribution <Astrict />
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <GlobalTextfield
                                                                    variant="outlined"
                                                                    placeholder=''
                                                                    size="medium"
                                                                    fullWidth
                                                                    autoComplete='off'
                                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                                    onKeyPress={allowOnlyDigits}
                                                                    value={finalAccumulationData?.typicalPlan?.user_tpi_contribution_data?.[index]?.annualContribution}
                                                                    onChange={(e) => {
                                                                        handleArrayInputChange("typicalPlan", "user_tpi_contribution_data", index, "annualContribution", e?.target?.value)
                                                                    }}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <InputAdornment position="start">
                                                                                <IconsColor iconName="dollarIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                            </InputAdornment>
                                                                        ),
                                                                    }}
                                                                />
                                                                <label className={globalStyles.helperTextStyle}>
                                                                    {formErrors?.typicalPlan?.user_tpi_contribution_data?.[index]?.annualContribution}
                                                                </label>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>

                                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                        <Grid container>
                                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                                    Duration (Years) <Astrict />
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <GlobalTextfield
                                                                    variant="outlined"
                                                                    placeholder='Enter Years'
                                                                    size="medium"
                                                                    fullWidth
                                                                    autoComplete='off'
                                                                    onKeyPress={allowOnlyDigits}
                                                                    value={finalAccumulationData?.typicalPlan?.user_tpi_contribution_data?.[index]?.duration}
                                                                    onChange={(e) => {
                                                                        handleArrayInputChange("typicalPlan", "user_tpi_contribution_data", index, "duration", e?.target?.value)
                                                                    }}
                                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                                />
                                                                <label className={globalStyles.helperTextStyle}>
                                                                    {formErrors?.typicalPlan?.user_tpi_contribution_data?.[index]?.duration}
                                                                </label>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>

                                                    {annualPremiumTypical?.length >= 2 &&
                                                        <Grid item sx={{ mt: "25px" }}>
                                                            <IconButton
                                                                onClick={() => handleRemoveSource(index)}
                                                            >
                                                                <CancelIcon sx={{ color: '#3B78CE' }} />
                                                            </IconButton>
                                                        </Grid>
                                                    }
                                                </Grid>
                                            </Grid>
                                        )
                                    })}


                                    <Grid item xs={12}>
                                        <Grid container className={`${globalStyles.globalPFFieldContainer}`}>
                                            <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                            Current Account Balance <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            onKeyPress={allowOnlyDigits}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <IconsColor iconName="dollarIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            onChange={(e) => handleInputChange("typicalPlan", "currentAccountBalance", e?.target?.value)}
                                                            value={finalAccumulationData?.typicalPlan?.currentAccountBalance}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors?.typicalPlan?.currentAccountBalance}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                            Percent Fees (%) <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            onKeyPress={allowDigitsAndDot}
                                                            onChange={(e) => handleInputChange("typicalPlan", "percentFees", e?.target?.value)}
                                                            value={finalAccumulationData?.typicalPlan?.percentFees}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors?.typicalPlan?.percentFees}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                            Employer Match (Annual $ Contribution) <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            onKeyPress={allowOnlyDigits}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <IconsColor iconName="dollarIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            onChange={(e) => handleInputChange("typicalPlan", "employerMatch", e?.target?.value)}
                                                            value={finalAccumulationData?.typicalPlan?.employerMatch}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors?.typicalPlan?.employerMatch}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                            Rate of Return during Retirement (%) <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            onKeyPress={allowDigitsAndDot}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            onChange={(e) => handleInputChange("typicalPlan", "rateOfReturnDuringRetirement", e?.target?.value)}
                                                            value={finalAccumulationData?.typicalPlan?.rateOfReturnDuringRetirement}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors?.typicalPlan?.rateOfReturnDuringRetirement}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                            Rate of Return during Working Years (%) <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            onKeyPress={allowDigitsAndDot}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            onChange={(e) => handleInputChange("typicalPlan", "rateOfReturnDuringWorkingYears", e?.target?.value)}
                                                            value={finalAccumulationData?.typicalPlan?.rateOfReturnDuringWorkingYears}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors?.typicalPlan?.rateOfReturnDuringWorkingYears}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                            Income Tax Rate (Before Retirement) (%) <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            onKeyPress={allowDigitsAndDot}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            onChange={(e) => handleInputChange("typicalPlan", "incomeTaxRateBeforeRetirement", e?.target?.value)}
                                                            value={finalAccumulationData?.typicalPlan?.incomeTaxRateBeforeRetirement}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors?.typicalPlan?.incomeTaxRateBeforeRetirement}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                                <Grid container>
                                                    <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                        <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                            Income Tax Rate (During Retirement) (%) <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <GlobalTextfield
                                                            variant="outlined"
                                                            placeholder=''
                                                            size="medium"
                                                            fullWidth
                                                            autoComplete='off'
                                                            stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                            onKeyPress={allowDigitsAndDot}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            onChange={(e) => handleInputChange("typicalPlan", "incomeTaxRateDuringRetirement", e?.target?.value)}
                                                            value={finalAccumulationData?.typicalPlan?.incomeTaxRateDuringRetirement}
                                                        />
                                                        <label className={globalStyles.helperTextStyle}>{formErrors?.typicalPlan?.incomeTaxRateDuringRetirement}</label>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* ASSIGN AGENT SECTION */}
                    {userRole != "agent" &&
                        <Grid item xs={12} className={`${globalStyles.globalPFBlurWrapper}`}>
                            <Grid container>
                                <Grid item xs={12} className={`${globalStyles.globalPFSectioHeadItem}`}>
                                    <Typography
                                        component={'span'}
                                        className={`${globalStyles.globalPFlabel}`}
                                    >
                                        Assign Profile
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={12} md={9} lg={11} xl={4.5}>
                                    <Grid container>
                                        <Grid item className={`${globalStyles.globalPFLabel}`}>
                                            <Typography
                                                component='span'
                                                className={`${globalStyles.globalPFTextfieldLabel}`}
                                            >
                                                Agent Name
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Autocomplete
                                                options={agentList}
                                                className={globalStyles.globalAutoCompleteStyle}
                                                onChange={handleAgentChange}
                                                value={finalAccumulationData?.AssignProfile?.agentName}
                                                renderInput={(params) => <TextField placeholder='Select Agent' {...params} />}
                                                sx={{
                                                    "& .MuiInputBase-root": {
                                                        padding: "3px !important"
                                                    }
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    }
                </Grid>
            </Grid>
        </Grid>
    )
}

export default ProfileNewAnnuityAccumulation